import {
  AmenityBookingShape,
  AmenityBookingStatus,
  AmenitySlot,
  ResidentAmenity,
  ResidentAmenityBooking,
} from '../../_types/resident'

// Amenity open hours + slots are authored/computed in this zone on the backend,
// so render slot + booking times in it too (the BUILDING's local time) rather
// than the renter's browser zone — otherwise the hours chip and the slot grid
// would disagree for a renter outside Eastern. Mirrors the backend default.
const AMENITY_TIMEZONE = 'America/Toronto'

const SLOT_TIME_FORMATTER = new Intl.DateTimeFormat('en-US', {
  timeZone: AMENITY_TIMEZONE,
  hour: 'numeric',
  minute: '2-digit',
})

const BOOKING_DAY_FORMATTER = new Intl.DateTimeFormat('en-US', {
  timeZone: AMENITY_TIMEZONE,
  weekday: 'short',
  month: 'short',
  day: 'numeric',
})

const SLOT_DAY_LABEL_FORMATTER = new Intl.DateTimeFormat('en-US', {
  timeZone: AMENITY_TIMEZONE,
  weekday: 'long',
  month: 'short',
  day: 'numeric',
})

const SLOT_DAY_KEY_FORMATTER = new Intl.DateTimeFormat('en-CA', {
  timeZone: AMENITY_TIMEZONE,
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
})

const DAY_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']

const BOOKING_STATUS_LABELS: Record<AmenityBookingStatus, string> = {
  'pending-payment': 'Payment pending',
  'pending-approval': 'Awaiting approval',
  confirmed: 'Confirmed',
  cancelled: 'Cancelled',
  declined: 'Declined',
}

const BOOKING_SHAPE_LABELS: Record<AmenityBookingShape, string> = {
  slot: 'Time slot',
  evening: 'Evening block',
  'full-day': 'Full day',
}

/**
 * Formats minutes-from-midnight as a 24h HH:MM string (e.g. 570 -> '09:30').
 *
 * @param {number} minutes - minutes from midnight
 * @returns {string}
 */
const minutesToTimeString = (minutes: number): string => {
  const hours = Math.floor(minutes / 60)
  const remainder = minutes % 60
  return `${String(hours).padStart(2, '0')}:${String(remainder).padStart(2, '0')}`
}

/**
 * Maps a booking shape to its renter-facing label.
 *
 * @param {AmenityBookingShape} shape - the amenity booking shape
 * @returns {string}
 */
const formatBookingShape = (shape: AmenityBookingShape): string =>
  BOOKING_SHAPE_LABELS[shape]

/**
 * One-line open-hours summary for an amenity (e.g. '09:00–22:00').
 *
 * @param {ResidentAmenity} amenity - the amenity
 * @returns {string}
 */
const formatAmenityHours = (amenity: ResidentAmenity): string =>
  `${minutesToTimeString(amenity.openMinutes)}–${minutesToTimeString(
    amenity.closeMinutes,
  )}`

/**
 * Short day-set summary ('Every day' when all seven, else 'Mon, Tue, …').
 *
 * @param {number[]} daysOfWeek - day indices (0=Sun..6=Sat)
 * @returns {string}
 */
const formatAmenityDays = (daysOfWeek: number[]): string => {
  if (daysOfWeek.length === 7) {
    return 'Every day'
  }
  return [...daysOfWeek]
    .sort((first, second) => first - second)
    .map(day => DAY_LABELS[day] ?? '')
    .filter(label => label !== '')
    .join(', ')
}

/**
 * Renter-facing deposit summary ('Free' or e.g. '$50.00 deposit').
 *
 * @param {ResidentAmenity} amenity - the amenity
 * @returns {string}
 */
const formatAmenityDeposit = (amenity: ResidentAmenity): string => {
  if (amenity.isPaid === true && amenity.depositAmountCents > 0) {
    return `$${(amenity.depositAmountCents / 100).toFixed(2)} deposit`
  }
  return 'Free'
}

/**
 * Formats a slot start time as a short local clock time (e.g. '5:00 PM').
 *
 * @param {number} startTime - epoch ms
 * @returns {string}
 */
const formatSlotTime = (startTime: number): string =>
  SLOT_TIME_FORMATTER.format(new Date(startTime))

/**
 * Formats a booking's window as "Sat, Jun 7 · 5:00–6:00 PM".
 *
 * @param {ResidentAmenityBooking} booking - the booking
 * @returns {string}
 */
const formatBookingWhen = (booking: ResidentAmenityBooking): string => {
  const day = BOOKING_DAY_FORMATTER.format(new Date(booking.startTime))
  const start = SLOT_TIME_FORMATTER.format(new Date(booking.startTime))
  const end = SLOT_TIME_FORMATTER.format(new Date(booking.endTime))
  return `${day} · ${start}–${end}`
}

/**
 * Maps a booking status to its renter-facing label.
 *
 * @param {AmenityBookingStatus} bookingStatus - the booking status
 * @returns {string}
 */
const formatBookingStatus = (bookingStatus: AmenityBookingStatus): string =>
  BOOKING_STATUS_LABELS[bookingStatus]

/**
 * Maps a booking status to a tag tone: confirmed (green), pending (amber, while
 * awaiting approval/payment), or ended (grey, cancelled/declined).
 *
 * @param {AmenityBookingStatus} bookingStatus - the booking status
 * @returns {'confirmed' | 'pending' | 'ended'}
 */
const getBookingStatusTone = (
  bookingStatus: AmenityBookingStatus,
): 'confirmed' | 'pending' | 'ended' => {
  if (bookingStatus === 'confirmed') {
    return 'confirmed'
  }
  if (bookingStatus === 'cancelled' || bookingStatus === 'declined') {
    return 'ended'
  }
  return 'pending'
}

/**
 * Groups available slots into per-day buckets (in display order) for a slot
 * picker. Slots are assumed already sorted by start time.
 *
 * @param {AmenitySlot[]} slots - the available slots
 * @returns {Array} per-day groups with a label and the day's slots
 */
const groupSlotsByDay = (
  slots: AmenitySlot[],
): { dayKey: string; dayLabel: string; slots: AmenitySlot[] }[] => {
  const groups: { dayKey: string; dayLabel: string; slots: AmenitySlot[] }[] =
    []
  slots.forEach(slot => {
    const dayKey = SLOT_DAY_KEY_FORMATTER.format(new Date(slot.startTime))
    const existing = groups.find(group => group.dayKey === dayKey)
    if (existing === undefined) {
      groups.push({
        dayKey,
        dayLabel: SLOT_DAY_LABEL_FORMATTER.format(new Date(slot.startTime)),
        slots: [slot],
      })
      return
    }
    existing.slots.push(slot)
  })
  return groups
}

export {
  formatBookingShape,
  formatAmenityHours,
  formatAmenityDays,
  formatAmenityDeposit,
  formatSlotTime,
  formatBookingWhen,
  formatBookingStatus,
  getBookingStatusTone,
  groupSlotsByDay,
}
